import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import { Search } from "lucide-react";
import Accordion from "../components/v2/accordion/accordion";
import CallToActionSmall from "../components/v2/call-to-action-sm";
import "../styles/scss/blog.module.scss";
import "../styles/scss/faq.module.scss";

type FaqItem = {
  id: number;
  question: string;
  answerHtml: string;
  answerPlain: string;
};

type FaqGroup = {
  id: string;
  title: string;
  faqs: FaqItem[];
};

function decodeHtml(value: string) {
  const namedEntities: Record<string, string> = {
    amp: "&",
    apos: "'",
    gt: ">",
    hellip: "…",
    ldquo: "“",
    lsquo: "‘",
    lt: "<",
    nbsp: " ",
    ndash: "–",
    quot: '"',
    rdquo: "”",
    rsquo: "’",
  };

  return value.replace(
    /&(#x?[0-9a-f]+|[a-z]+);/gi,
    (entity, code: string) => {
      if (code[0] === "#") {
        const isHex = code[1]?.toLowerCase() === "x";
        const value = Number.parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10);
        return Number.isNaN(value) ? entity : String.fromCodePoint(value);
      }
      return namedEntities[code.toLowerCase()] ?? entity;
    }
  );
}

function textFromHtml(value: string) {
  return decodeHtml(
    value
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/&[a-z0-9#]+;/gi, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "faq-section"
  );
}

function extractFaqs(html: string) {
  const candidates: Array<{
    index: number;
    question: string;
    answerHtml: string;
  }> = [];

  const addMatches = (
    regex: RegExp,
    questionIndex: number,
    answerIndex: number
  ) => {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(html)) !== null) {
      const question = textFromHtml(match[questionIndex] || "");
      const answerHtml = (match[answerIndex] || "").trim();
      if (question && answerHtml) {
        candidates.push({ index: match.index, question, answerHtml });
      }
    }
  };

  // Structured Content FAQ: section.sc_fs_faq > h2/h3 + .sc_fs_faq__content (or sc_fs_faq_content)
  addMatches(
    /<section\b[^>]*class=["'][^"']*\bsc_fs_faq\b[^"']*["'][^>]*>[\s\S]*?<h[2-6]\b[^>]*>([\s\S]*?)<\/h[2-6]>[\s\S]*?<div\b[^>]*class=["'][^"']*\bsc_fs_faq(?:__|_)content\b[^"']*["'][^>]*>([\s\S]*?)<\/div>[\s\S]*?<\/section>/gi,
    1,
    2
  );

  addMatches(
    /<(?:strong|h[2-6])\b[^>]*class=["'][^"']*\bschema-faq-question\b[^"']*["'][^>]*>([\s\S]*?)<\/(?:strong|h[2-6])>[\s\S]*?<(?:p|div)\b[^>]*class=["'][^"']*\bschema-faq-answer\b[^"']*["'][^>]*>([\s\S]*?)<\/(?:p|div)>/gi,
    1,
    2
  );

  addMatches(
    /<details\b[^>]*>[\s\S]*?<summary\b[^>]*>([\s\S]*?)<\/summary>([\s\S]*?)<\/details>/gi,
    1,
    2
  );

  if (candidates.length === 0) {
    addMatches(
      /<h3\b[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3\b|$)/gi,
      1,
      2
    );
  }

  const seen = new Set<string>();
  return candidates
    .sort((a, b) => a.index - b.index)
    .filter((candidate) => {
      const key = candidate.question.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((candidate, index) => ({
      id: index + 1,
      question: candidate.question,
      answerHtml: candidate.answerHtml,
      answerPlain: textFromHtml(candidate.answerHtml),
    }));
}

function parseFaqContent(content: string) {
  // Section titles are top-level wp-block-heading h2s.
  // FAQ questions are also h2s, but live inside .sc_fs_faq — ignore those.
  const headingRegex =
    /<h2\b([^>]*\bwp-block-heading\b[^>]*)>([\s\S]*?)<\/h2>/gi;
  const headings: Array<{
    attrs: string;
    title: string;
    index: number;
    end: number;
  }> = [];
  let match: RegExpExecArray | null;

  while ((match = headingRegex.exec(content)) !== null) {
    headings.push({
      attrs: match[1] || "",
      title: textFromHtml(match[2] || ""),
      index: match.index,
      end: headingRegex.lastIndex,
    });
  }

  // Fallback when WP headings lack wp-block-heading (exclude FAQ question h2s)
  if (headings.length === 0) {
    const withoutFaqs = content.replace(
      /<section\b[^>]*class=["'][^"']*\bsc_fs_faq\b[^"']*["'][^>]*>[\s\S]*?<\/section>/gi,
      (block) => " ".repeat(block.length)
    );
    const fallbackRegex = /<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi;
    while ((match = fallbackRegex.exec(withoutFaqs)) !== null) {
      headings.push({
        attrs: match[1] || "",
        title: textFromHtml(match[2] || ""),
        index: match.index,
        end: match.index + match[0].length,
      });
    }
  }

  const introHtml = headings.length > 0 ? content.slice(0, headings[0].index) : "";
  const introMatch = introHtml.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
  const intro = introMatch ? textFromHtml(introMatch[1]) : "";
  const usedIds = new Set<string>();

  const groups = headings
    .map((heading, index) => {
      const sectionHtml = content.slice(
        heading.end,
        headings[index + 1]?.index ?? content.length
      );
      const explicitId = heading.attrs.match(/\bid=["']([^"']+)["']/i)?.[1];
      const baseId = explicitId || slugify(heading.title);
      let id = baseId;
      let suffix = 2;
      while (usedIds.has(id)) id = `${baseId}-${suffix++}`;
      usedIds.add(id);

      return {
        id,
        title: heading.title,
        faqs: extractFaqs(sectionHtml),
      };
    })
    .filter((group) => group.title && group.faqs.length > 0);

  return { intro, groups };
}

export default function FaqPage({ pageData }) {
  const page = pageData?.pages?.edges?.[0]?.node;
  const { intro, groups } = useMemo(
    () => parseFaqContent(page?.content || ""),
    [page?.content]
  );
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState(groups[0]?.id || "");

  const filteredGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return groups;

    return groups
      .map((group) => ({
        ...group,
        faqs: group.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(normalizedQuery) ||
            faq.answerPlain.toLowerCase().includes(normalizedQuery) ||
            group.title.toLowerCase().includes(normalizedQuery)
        ),
      }))
      .filter((group) => group.faqs.length > 0);
  }, [groups, query]);

  useEffect(() => {
    if (query.trim()) return;

    const sections = groups
      .map((group) => document.getElementById(group.id))
      .filter((section): section is HTMLElement => Boolean(section));
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) setActiveGroup(visible.target.id);
      },
      { rootMargin: "-20% 0px -65% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [groups, query]);

  return (
    <>
      <div className="faq-page product-updates-page">
        <section className="product-updates-hero">
          <div className="container">
            <div className="product-updates-hero-content">
              <div className="product-updates-hero-copy">
                <h1>Frequently Asked Questions</h1>
                <p>
                  {intro ||
                    "Learn more about Middleware, and how we reduce costs"}
                </p>
              </div>
              <div className="product-updates-search faq-page-search-wrap">
                <label className="faq-page-search">
                  <Search size={20} strokeWidth={1.5} aria-hidden="true" />
                  <input
                    type="search"
                    aria-label="Search frequently asked questions"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by name, tag, label or annotation"
                  />
                </label>
              </div>
            </div>
          </div>
        </section>

        <section className="faq-page-content">
          <div className="container">
            <div className="faq-page-layout">
              <nav className="faq-page-nav" aria-label="FAQ categories">
                {(query.trim() ? filteredGroups : groups).map((group) => (
                  <a
                    key={group.id}
                    href={`#${group.id}`}
                    className={activeGroup === group.id ? "active" : ""}
                    onClick={() => setActiveGroup(group.id)}
                  >
                    {group.title}
                  </a>
                ))}
              </nav>

              <div className="faq-page-groups">
                {filteredGroups.length > 0 ? (
                  filteredGroups.map((group) => (
                    <section
                      className="faq-page-group"
                      id={group.id}
                      key={group.id}
                    >
                      <h2>{group.title}</h2>
                      <div className="faq-page-accordion">
                        <Accordion
                          key={`${group.id}-${query}`}
                          faqs={group.faqs.map((faq) => ({
                            id: faq.id,
                            header: faq.question,
                            answerPlain: faq.answerPlain,
                            text: parse(faq.answerHtml),
                          }))}
                          showGroups={true}
                          defaultActiveId={group.faqs[0]?.id ?? null}
                        />
                      </div>
                    </section>
                  ))
                ) : (
                  <div className="faq-page-empty">
                    No frequently asked questions match your search.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <CallToActionSmall title="When Your Code Breaks, We Will Be There To Fix" />
    </>
  );
}
